// src/components/CustomerView/CustomerViewPopup.tsx
import React from 'react';
import ViewModal from '../Modals/ViewModal';
import { useCustomer } from '../../hooks/useCustomer';
import { EntityType } from '../../constants/entityType';
import {
  FinalConsumerView,
  GeneralCustomerView,
  LegalPersonView,
  NaturalPersonView,
} from './CustomerView/sections';

interface CustomerViewPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const CustomerViewPopup: React.FC<CustomerViewPopupProps> = ({
  isOpen,
  onClose,
}) => {
  const { detail } = useCustomer();

  const renderByEntityType = () => {
    if (!detail) return null;

    switch (detail.entityTypeId) {
      case EntityType.PERSON_NATURAL:
        return <NaturalPersonView detail={detail} />;
      case EntityType.PERSON_LEGAL:
        return <LegalPersonView detail={detail} />;
      case EntityType.CONSUMER_FINAL:
        return <FinalConsumerView detail={detail} />;
      case EntityType.GENERAL:
        return <GeneralCustomerView detail={detail} />;
      default:
        // Fallback seguro (si llega un tipo desconocido)
        return <FinalConsumerView detail={detail} />;
    }
  };

  return (
    <ViewModal
      popupOpen={isOpen}
      setPopupOpen={onClose}
      title="Detalle del Cliente"
      maxWidth="max-w-5xl"
    >
      {detail ? (
        <>
          {/* Encabezado */}
          <div className="mb-8">
            <h2 className="text-gray-800 text-2xl font-bold dark:text-white">
              {detail.name || detail.legalName || 'Sin nombre'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm">
              Tipo de Cliente: {detail.entityTypeName ?? '—'}
            </p>
            <span
              className={`mt-3 inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                detail.status
                  ? 'bg-green-500 text-white'
                  : 'bg-[#DC3545] text-white'
              }`}
            >
              {detail.status ? 'Activo' : 'Inactivo'}
            </span>
            {detail.defaultCustomer && (
              <span className="ml-2 mt-3 inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                Predeterminado
              </span>
            )}
          </div>

          {/* Contenido específico por tipo */}
          {renderByEntityType()}

          {/* Botón de cierre */}
          <button
            onClick={onClose}
            className="mt-8 w-full rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            Cerrar
          </button>
        </>
      ) : (
        <div className="text-gray-600 dark:text-gray-300 text-center">
          <p>No hay información para mostrar por el momento.</p>
          <button
            onClick={onClose}
            className="mt-6 w-full rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            Cerrar
          </button>
        </div>
      )}
    </ViewModal>
  );
};

export default CustomerViewPopup;
